import type { PortfolioImportMapping } from '@workspace/types';

const REQUIRED_FIELDS: Array<{
  key: keyof PortfolioImportMapping;
  label: string;
  hint: string;
  aliases: string[];
}> = [
  {
    key: 'invoiceNumber',
    label: 'Nro. factura',
    hint: 'Identificador único de la factura o comprobante',
    aliases: ['nro_factura', 'factura', 'comprobante', 'invoice', 'numero_factura'],
  },
  {
    key: 'amount',
    label: 'Monto original',
    hint: 'Importe total de la factura',
    aliases: ['monto', 'importe', 'amount', 'total'],
  },
  {
    key: 'dueDate',
    label: 'Vencimiento',
    hint: 'Fecha de vencimiento (dd/mm/aaaa o aaaa-mm-dd)',
    aliases: ['vencimiento', 'fecha_venc', 'due_date', 'duedate', 'fecha'],
  },
];

const OPTIONAL_FIELDS: Array<{
  key: keyof PortfolioImportMapping;
  label: string;
  hint: string;
  aliases: string[];
}> = [
  {
    key: 'firstName',
    label: 'Nombre',
    hint: 'Nombre de pila del contacto',
    aliases: ['nombre', 'first_name', 'firstname'],
  },
  {
    key: 'lastName',
    label: 'Apellido',
    hint: 'Apellido del contacto',
    aliases: ['apellido', 'last_name', 'lastname'],
  },
  {
    key: 'businessName',
    label: 'Razón social',
    hint: 'Empresa o titular',
    aliases: ['razon_social', 'empresa', 'business', 'cliente'],
  },
  {
    key: 'balance',
    label: 'Saldo',
    hint: 'Si no se indica, se usa el monto original',
    aliases: ['saldo', 'balance', 'deuda', 'pendiente'],
  },
  {
    key: 'phone',
    label: 'Teléfono',
    hint: 'Opcional si hay email. Número con código de área.',
    aliases: ['telefono', 'tel', 'celular', 'phone', 'movil', 'whatsapp'],
  },
  {
    key: 'email',
    label: 'Email',
    hint: 'Opcional si hay teléfono. Requerido uno de los dos.',
    aliases: ['email', 'mail', 'correo'],
  },
  {
    key: 'seller',
    label: 'Vendedor',
    hint: 'Código o nombre del vendedor',
    aliases: ['vendedor', 'seller', 'comercial'],
  },
  {
    key: 'branch',
    label: 'Sucursal',
    hint: 'Sucursal o zona',
    aliases: ['sucursal', 'branch', 'zona', 'local'],
  },
];

export const IMPORT_FIELD_GROUPS = [
  { title: 'Obligatorios', fields: REQUIRED_FIELDS },
  { title: 'Opcionales', fields: OPTIONAL_FIELDS },
] as const;

function normalizeHeader(value: string): string {
  return value
    .slice(0, 64)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function guessColumn(columns: string[], aliases: string[]): string {
  const normalized = columns.map(column => ({ raw: column, key: normalizeHeader(column) }));
  for (const alias of aliases) {
    const match = normalized.find(item => item.key === alias || item.key.includes(alias));
    if (match) {
      return match.raw;
    }
  }
  return '';
}

export function guessImportMapping(columns: string[]): PortfolioImportMapping {
  const mapping: PortfolioImportMapping = {
    phone: '',
    invoiceNumber: '',
    amount: '',
    dueDate: '',
  };
  for (const group of IMPORT_FIELD_GROUPS) {
    for (const field of group.fields) {
      const guessed = guessColumn(columns, field.aliases);
      if (guessed) {
        mapping[field.key] = guessed;
      }
    }
  }
  return mapping;
}

export const DEFAULT_IMPORT_MAPPING: PortfolioImportMapping = {
  phone: 'telefono',
  invoiceNumber: 'nro_factura',
  amount: 'monto',
  dueDate: 'vencimiento',
  firstName: 'nombre',
  lastName: 'apellido',
  businessName: 'razon_social',
  balance: 'saldo',
  seller: 'vendedor',
  branch: 'sucursal',
};

export function mappingIsComplete(mapping: PortfolioImportMapping): boolean {
  const hasContact = Boolean(mapping.phone?.trim() || mapping.email?.trim());
  return Boolean(hasContact && mapping.invoiceNumber && mapping.amount && mapping.dueDate);
}
